import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../i18n";
import { payments as paymentsApi, bookings as bookingsApi } from "../damsApi";
import { DepositCard } from "../components/DepositPolicy";
import {
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  Calendar,
  Home as HomeIcon,
  Loader2,
  XCircle,
  Gauge,
} from "lucide-react";
import { toast } from "sonner";

// Poll Stripe payment status: 0.5s, 1s, 1.5s, 2s, 2.5s ... up to ~30s
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15;

export default function Confirmation() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const raw = localStorage.getItem("dams_last_reservation");
  const initial = raw ? JSON.parse(raw) : null;
  const [reservation, setReservation] = useState(initial);
  const [polling, setPolling] = useState(Boolean(sessionId));
  const [pollState, setPollState] = useState({
    payment_status: null,
    status: null,
    expired: false,
    error: null,
  });
  const attempts = useRef(0);

  // If Stripe redirected us back with session_id, poll for payment status.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer;

    const tick = async () => {
      attempts.current += 1;
      try {
        const data = await paymentsApi.status(sessionId);
        if (cancelled) return;
        setPollState({
          payment_status: data.payment_status,
          status: data.status,
          expired: data.status === "expired",
          error: null,
        });
        const settled =
          data.payment_status === "paid" ||
          data.status === "complete" ||
          data.status === "expired";
        if (settled) {
          setPolling(false);
          // Refresh reservation snapshot from the latest booking on the server.
          if (initial?.booking_id) {
            try {
              const fresh = await bookingsApi.get(initial.booking_id);
              const updated = {
                ...initial,
                status: fresh.status,
                payment_method: fresh.payment_method,
                total_price: fresh.total_price,
                deposit: fresh.deposit,
                extra_mileage_fee: fresh.extra_mileage_fee,
                extra_mileage_km: fresh.extra_mileage_km,
                extra_mileage_rate: fresh.extra_mileage_rate,
                odometer_in: fresh.odometer_in,
                odometer_out: fresh.odometer_out,
              };
              setReservation(updated);
              localStorage.setItem("dams_last_reservation", JSON.stringify(updated));
            } catch (err) {
              console.warn("Could not refresh booking snapshot:", err);
            }
          }
          return;
        }
        if (attempts.current >= MAX_POLL_ATTEMPTS) {
          setPolling(false);
          setPollState((s) => ({ ...s, error: "Still waiting for Stripe — refresh in a moment." }));
          return;
        }
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setPolling(false);
        setPollState((s) => ({
          ...s,
          error: e?.response?.data?.detail || e.message || "Status check failed",
        }));
      }
    };

    timer = setTimeout(tick, 500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!reservation && !sessionId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold">No reservation found.</h2>
        <button className="pill-btn primary mt-4" onClick={() => navigate("/")}>
          {t("confirm.backHome")}
        </button>
      </div>
    );
  }

  const r = reservation || {};
  const isStripeReturn = Boolean(sessionId);
  const isPaidStripe = isStripeReturn && pollState.payment_status === "paid";
  const isExpired = isStripeReturn && pollState.expired;
  const isPendingCash = !isStripeReturn && r.payment_method === "cash";

  let header;
  if (polling) {
    header = {
      icon: <Loader2 className="animate-spin" size={40} />,
      iconClass: "bg-gray-100 text-gray-500",
      title: "Confirming your payment…",
      subtitle: "This usually takes a few seconds.",
    };
  } else if (isPaidStripe) {
    header = {
      icon: <CheckCircle2 size={40} />,
      iconClass: "bg-emerald-100 text-emerald-600",
      title: t("confirm.title"),
      subtitle: r.user_email
        ? t("confirm.subtitle", { email: r.user_email })
        : "Your reservation is locked in. A copy of your receipt was emailed to you.",
    };
  } else if (isExpired) {
    header = {
      icon: <XCircle size={40} />,
      iconClass: "bg-red-100 text-red-600",
      title: "Payment was not completed",
      subtitle:
        "Your Stripe Checkout session expired or was cancelled. Your reservation is still on hold — try again or pick a different method.",
    };
  } else if (isPendingCash) {
    header = {
      icon: <Clock size={40} />,
      iconClass: "bg-amber-100 text-amber-600",
      title: t("confirm.pendingTitle"),
      subtitle:
        "Bring the exact amount in cash at pick-up. Your booking is held with status PENDING PAYMENT.",
    };
  } else {
    header = {
      icon: <CheckCircle2 size={40} />,
      iconClass: "bg-emerald-100 text-emerald-600",
      title: t("confirm.title"),
      subtitle: "Your reservation has been received.",
    };
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <div
          className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${header.iconClass}`}
        >
          {header.icon}
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="confirmation-title">
          {header.title}
        </h1>
        <p className="text-gray-600 mt-3 max-w-md mx-auto">{header.subtitle}</p>
        {pollState.error && (
          <p className="text-xs text-red-500 mt-2">{pollState.error}</p>
        )}
      </div>

      {reservation && (
        <div className="card-soft p-6">
          <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">{t("confirm.reservation")}</p>
              <p className="font-extrabold text-lg">
                #{(r.booking_id || "").slice(-8).toUpperCase()}
              </p>
            </div>
            <span
              className={`badge ${
                isPaidStripe
                  ? "badge-green"
                  : isExpired
                  ? "badge-red"
                  : isPendingCash
                  ? "badge-amber"
                  : polling
                  ? "badge-gray"
                  : "badge-blue"
              }`}
            >
              {polling
                ? "Verifying…"
                : isPaidStripe
                ? "PAID"
                : isExpired
                ? "PAYMENT EXPIRED"
                : (r.status || "RECEIVED").toUpperCase().replace(/_/g, " ")}
            </span>
          </div>

          <h3 className="font-bold mb-3">{t("confirm.summary")}</h3>
          <div className="flex gap-4 mb-4">
            {r.car_image && (
              <img src={r.car_image} alt="" className="w-28 h-20 rounded-lg object-cover" />
            )}
            <div>
              <p className="font-bold text-lg leading-tight">{r.car_name}</p>
              <p className="text-sm text-gray-500">{r.user_name}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="card-soft p-4" style={{ background: "#fafafa" }}>
              <p className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
                <Calendar size={11} /> {t("detail.pickup")}
              </p>
              <p className="font-semibold mt-1">{r.pickup_date}</p>
            </div>
            <div className="card-soft p-4" style={{ background: "#fafafa" }}>
              <p className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
                <Calendar size={11} /> {t("detail.dropoff")}
              </p>
              <p className="font-semibold mt-1">{r.dropoff_date}</p>
            </div>
            {r.pickup_location_name && (
              <div className="card-soft p-4 sm:col-span-2" style={{ background: "#fafafa" }}>
                <p className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
                  <MapPin size={11} /> {t("detail.pickup")}
                </p>
                <p className="font-semibold mt-1">{r.pickup_location_name}</p>
              </div>
            )}
          </div>

          <div className="subtle-divider mt-5" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t("detail.subtotal")}</span>
              <span>${Number(r.subtotal || 0).toFixed(2)}</span>
            </div>
            {Number(r.discount_amount || 0) > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Promo discount</span>
                <span>−${Number(r.discount_amount).toFixed(2)}</span>
              </div>
            )}
            {Number(r.refuel_amount || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Refuel</span>
                <span>+${Number(r.refuel_amount).toFixed(2)}</span>
              </div>
            )}
            {Number(r.extra_mileage_fee || 0) > 0 && (
              <div className="flex justify-between" style={{ color: "#a05a00" }}>
                <span>
                  Extra mileage
                  {r.extra_mileage_km && r.extra_mileage_rate
                    ? ` (${r.extra_mileage_km} km × $${Number(r.extra_mileage_rate).toFixed(2)})`
                    : ""}
                </span>
                <span className="font-semibold">+${Number(r.extra_mileage_fee).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t("detail.tax")} ({Number(r.tax_rate || 0)}%)
              </span>
              <span>${Number(r.tax_amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base pt-1">
              <span className="font-bold">{t("detail.total")}</span>
              <span className="font-extrabold text-red-600 text-xl">
                ${Number(r.total_price || 0).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Refundable deposit — shown SEPARATELY from total */}
          <DepositCard amount={r.deposit} variant="dark" />

          {/* Rental details: odometer readings (filled by admin at pickup / drop-off) */}
          {(r.odometer_in != null || r.odometer_out != null) && (
            <div className="mt-4 card-soft p-4" style={{ background: "#fafafa" }}>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5 mb-3">
                <Gauge size={12} /> Rental details
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Odometer at pickup</p>
                  <p className="font-bold mt-0.5">
                    {r.odometer_in != null ? `${r.odometer_in.toLocaleString()} km` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Odometer at drop-off</p>
                  <p className="font-bold mt-0.5">
                    {r.odometer_out != null ? `${r.odometer_out.toLocaleString()} km` : "—"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {r.user_email && isPaidStripe && (
            <div className="mt-5 flex items-center gap-2 text-sm text-gray-600">
              <Mail size={14} /> A copy was sent to <span className="font-semibold">{r.user_email}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button
          onClick={() => navigate("/")}
          className="pill-btn dark flex-1 inline-flex items-center justify-center gap-2"
          style={{ padding: "0.85rem" }}
          data-testid="go-home"
        >
          <HomeIcon size={14} /> {t("confirm.backHome")}
        </button>
        <button
          onClick={() => navigate("/my-bookings")}
          className="pill-btn outline flex-1"
          style={{ padding: "0.85rem" }}
        >
          My bookings
        </button>
      </div>
    </div>
  );
}
